import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { Router } from "@angular/router";
import { RequestService } from "./request.service";
import { environment } from "../../../environments/environment";
import { SignalingService } from "./signaling.service";
import { previewChatMessage } from "../utils/chat-content";

export interface ChatData {
    show: boolean;
    focusToSearch?: boolean;
    caseId: string;
    caseName: string;
    investigators: any;
    investigatorGroups: any;
    isInvestigatorProfile?: boolean;
    isNotHiddenLeftMenu?: boolean;
}

export interface CallData {
    show: boolean;
    answerCall?: boolean;
    answerSessionCall?: string;
    currentCaseId?: string;
    currentCaseInvestigator?: any;
    currentInvestigatorGroup?: any;
    caseId?: string;
    investigators?: any;
    selectedUsers?: any;
    hostId?: string;
    userId?: string;
    invitedBy?: string;
    invitedByName?: string;
    callerId?: string;
    investigatorGroups?: any;
    isInvestigatorProfile?: boolean;
    invitees?: string[];
    isVideoOn?: boolean;
    isAudioOn?: boolean;
    isCaptionOn?: boolean;
    isMinimized?: boolean;
    archive?: any;
    isRecording?: boolean;
    isRestoring?: boolean;
    callSessionId?: string;
}

@Injectable()
export class ChatService {
    public autoSelectedChat: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public selectedUser: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public selectedDashboardUser: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public selectedChatUser: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public callStatus: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public unreadSubject: BehaviorSubject<any> = new BehaviorSubject<any>([]);
    public unreadGroupSubject: BehaviorSubject<any> = new BehaviorSubject<any>([]);
    public popupChat: Subject<CallData> = new Subject<CallData>();
    public pinnedChats: Subject<any> = new Subject<any>();
    public showChatDialogSubject: BehaviorSubject<ChatData> = new BehaviorSubject<ChatData>({
        show: undefined,
        caseId: '',
        caseName: '',
        investigatorGroups: [],
        investigators: [],
    });
    public showCallDialogSubject: BehaviorSubject<CallData> = new BehaviorSubject<CallData>({
        show: false
    });
    public showDashboardChatDialogSubject: BehaviorSubject<ChatData> = new BehaviorSubject<ChatData>({
        show: undefined,
        caseId: '',
        caseName: '',
        investigatorGroups: [],
        investigators: [],
    });
    public showCallPromptSubject: BehaviorSubject<CallData> = new BehaviorSubject<CallData>({
        show: false
    });
    // public showJitsiCallDialogSubject: BehaviorSubject<CallData> = new BehaviorSubject<CallData>({
    //     show: false
    // });
    private _ongoingCall: any = undefined;
    public get ongoingCall(): any {
        return this._ongoingCall;
    }
    public set ongoingCall(value: any) {
        if (this._ongoingCall === value) return;
        this._ongoingCall = value;
        if (value) {
            this.callSyncChannel.postMessage({ type: 'CALL_STARTED', payload: value });
        } else {
            this.callSyncChannel.postMessage({ type: 'CALL_ENDED' });
        }
    }

    private db: any = undefined;
    private callSyncChannel = new BroadcastChannel('call_sync_channel');

    constructor(private router: Router, private requestService: RequestService, private signalingService: SignalingService) {
        this.setupCallSync();
    }

    private normalizeId(value: any): string {
        if (value === undefined || value === null) return '';
        return value.toString();
    }

    private isSameId(a: any, b: any): boolean {
        const left = this.normalizeId(a);
        const right = this.normalizeId(b);
        return !!left && !!right && left === right;
    }

    /** A case's "Message All" thread is identified by that case ID. */
    isGeneralChatMessage(message: any): boolean {
        if (!message) return false;
        if (message.isGeneralChat === true || message.isGeneralChat === 'true') return true;
        if (message.caseId && message.receiverId && message.caseId === message.receiverId) return true;
        return false;
    }

    private setupCallSync() {
        this.callSyncChannel.onmessage = (event) => {
            if (event.data?.type === 'CALL_STARTED') {
                this._ongoingCall = event.data.payload;
            } else if (event.data?.type === 'CALL_ENDED') {
                this._ongoingCall = undefined;
            } else if (event.data?.type === 'QUERY_ONGOING_CALL') {
                if (this._ongoingCall) {
                    this.callSyncChannel.postMessage({ type: 'CALL_STARTED', payload: this._ongoingCall });
                }
            }
        };
        // Query other tabs on startup
        this.callSyncChannel.postMessage({ type: 'QUERY_ONGOING_CALL' });
    }

    public setOngoingCall(value: any) {
        this.ongoingCall = value;
    }

    getUnreadMessages() {
        let url = environment.serverUrl + 'chat/unread';
        url = this.requestService.addLangToURL(url, environment.lang);

        this.requestService.jsonGetRequest(url, (data, error) => {
            if (error) {
                console.error('Failed to get unread messages', error);
                return;
            }
            if (data && data.results) {
                // Group by senderId/caseId logic similar to what unread-chat-dialog expects
                const messages = data.results;
                const grouped = this.groupMessages(messages);
                this.unreadSubject.next(grouped);
            }
        });
    }

    private groupMessages(messages: any[]) {
        if (!Array.isArray(messages)) {
            // If messages is not an array (e.g. wrapper object), try to find the array inside or return empty
            if (messages && (messages as any).results && Array.isArray((messages as any).results)) {
                messages = (messages as any).results;
            } else {
                console.warn('ChatService.groupMessages: messages is not an array', messages);
                return [];
            }
        }

        const groupedMap = new Map<string, any>();

        messages.forEach(msg => {
            const isGroup = msg?.isGroup === true || msg?.isGroup === 'true' || msg?.isGeneralChat === true || msg?.isGeneralChat === 'true';
            const isGeneral = this.isGeneralChatMessage(msg);
            const targetId = isGeneral
                ? (msg?.receiverId || msg?.caseId)
                : (isGroup ? (msg?.receiverId || msg?.groupId) : (msg?.partnerId || msg?.senderId));
            const unreadCount = Number(msg?.count || 0);
            if (!targetId) return;
            const createdAt = msg?.createdAt || msg?.created;
            const key = isGeneral
                ? `case_general_${msg.caseId || targetId}`
                : (msg.caseId ? `case_${msg.caseId}_${targetId}` : `user_${targetId}`);
            if (!groupedMap.has(key)) {
                groupedMap.set(key, {
                    ...msg,
                    count: 0,
                    id: targetId,
                    isGroup: isGroup,
                    createdAt: createdAt,
                    lastMessage: previewChatMessage(msg)
                });
            }
            const current = groupedMap.get(key);
            current.count += unreadCount;
            if (createdAt && (!current.createdAt || new Date(createdAt).getTime() > new Date(current.createdAt).getTime())) {
                current.createdAt = createdAt;
                current.content = previewChatMessage(msg) || current.content;
                current.lastMessage = previewChatMessage(msg) || current.lastMessage;
            }
        });

        const result = Array.from(groupedMap.values())
            .filter(item => Number(item?.count || 0) > 0);
        result.sort((a, b) => {
            const ua = Number(a?.count || 0) > 0 ? 1 : 0;
            const ub = Number(b?.count || 0) > 0 ? 1 : 0;
            if (ub !== ua) {
                return ub - ua;
            }
            return new Date(b.createdAt || b.created).getTime() - new Date(a.createdAt || a.created).getTime();
        });
        return result;
    }

    showChatDialog(senderId, senderName, shouldClear: boolean = false) {
        this.router.navigate(([`/notifications`])).then(() => {
            this.selectedUser.next({
                isGroup: false,
                id: senderId,
                name: senderName,
                shouldClear: shouldClear,
            });

            // let tokens = [];
            // userTokens.deviceids?.map(j => tokens.push(j.deviceId));

            this.showChatDialogSubject.next({
                show: true,
                investigators: [{ _id: senderId, name: senderName }],
                caseId: undefined,
                caseName: undefined,
                investigatorGroups: [],
                isInvestigatorProfile: true,
            });
        });
    }

    async markAsRead(currentUserId, senderId, isGroup, caseId?) {
        const unread = this.unreadSubject.getValue() || [];
        const filtered = unread.filter((item: any) => {
            const itemIsGroup = item?.isGroup === true || item?.isGroup === 'true';
            if (isGroup) {
                // A group can be assigned to several cases — only clear the
                // unread entry for this group within the case opened (#1117).
                const sameGroup = this.isSameId(item.id, senderId);
                const sameCase = !caseId || this.isSameId(item.caseId, caseId);
                return !(itemIsGroup && sameGroup && sameCase);
            }
            if (itemIsGroup) {
                return true;
            }
            const sameSender = this.isSameId(item.id, senderId);
            const sameDmCase = !caseId || this.isSameId(item.caseId, caseId);
            return !(sameSender && sameDmCase);
        });
        this.unreadSubject.next(filtered);

        // Call backend to persist read status
        const payload: any = {
            senderId: senderId,
            isGroup: isGroup,
        };

        // Only send caseId for Group chats where it might be relevant context
        // For Direct Messages, we want to clear ALL unread from this sender globally
        if (caseId) {
            payload.caseId = caseId;
        }

        let url = environment.serverUrl + 'chat/markAsRead';
        url = this.requestService.addLangToURL(url, environment.lang);

        this.requestService.jsonRequest(url, (data, error) => {
            if (error) {
                console.error('Failed to mark as read', error);
            } else {
                // Success - refresh from server to ensure sync
                this.getUnreadMessages();
            }
        }, 'POST', payload);
    }

    handleSentMessage(message: any) {
        const unread = this.unreadSubject.getValue() || [];
        let found = false;
        const currentUserId = this.requestService.currentUser?._id;
        const isGroup = message?.isGroup === true || message?.isGroup === 'true' || message?.isGeneralChat === true || message?.isGeneralChat === 'true';
        const isGeneralChat = this.isGeneralChatMessage(message);

        const targetId = isGeneralChat ? (message?.receiverId || message?.caseId) : (isGroup ? (message?.groupId || message?.receiverId) : message?.receiverId);

        const updated = unread.map(item => {
            const itemIsGroup = item?.isGroup === true || item?.isGroup === 'true';
            const sameCase = !message?.caseId || this.isSameId(item?.caseId, message.caseId);
            if (isGroup) {
                if (itemIsGroup && sameCase && this.isSameId(item.id, targetId)) {
                    found = true;
                    return { ...item, createdAt: message.createdAt || new Date().toISOString(), lastMessage: previewChatMessage(message) };
                }
            } else {
                if (!itemIsGroup && sameCase && this.isSameId(item.id, targetId)) {
                    found = true;
                    return { ...item, createdAt: message.createdAt || new Date().toISOString(), lastMessage: previewChatMessage(message) };
                }
            }
            return item;
        });

        if (!found) {
            updated.push({
                ...message,
                count: 0,
                id: targetId,
                isGroup: isGroup,
                caseName: message.caseName,
                createdAt: message.createdAt || new Date().toISOString(),
                lastMessage: previewChatMessage(message)
            });
        }

        // Sort by createdAt
        updated.sort((a, b) => new Date(b.createdAt || b.created).getTime() - new Date(a.createdAt || a.created).getTime());

        // Keep unread list strictly unread-only to avoid ghost "0 unread" artifacts.
        this.unreadSubject.next(updated.filter(item => Number(item?.count || 0) > 0));
    }

    handleNewMessage(message: any) {
        const unread = this.unreadSubject.getValue() || [];
        let found = false;
        const currentUserId = this.requestService.currentUser?._id;
        const isGroup = message?.isGroup === true || message?.isGroup === 'true' || message?.isGeneralChat === true || message?.isGeneralChat === 'true';
        const isGeneralChat = this.isGeneralChatMessage(message);

        // Ignore messages sent by the current user to prevent them from showing as unread
        if (message?.senderId === currentUserId) return;

        const targetId = isGeneralChat ? (message?.receiverId || message?.caseId) : (isGroup ? (message?.groupId || message?.receiverId) : message?.senderId);

        const updated = unread.map(item => {
            const itemIsGroup = item?.isGroup === true || item?.isGroup === 'true';
            const sameCase = !message?.caseId || this.isSameId(item?.caseId, message.caseId);
            if (isGroup) {
                if (itemIsGroup && sameCase && this.isSameId(item.id, targetId)) {
                    found = true;
                    return { ...item, count: item.count + 1, createdAt: message.createdAt || new Date().toISOString(), lastMessage: previewChatMessage(message) };
                }
            } else {
                if (!itemIsGroup && sameCase && this.isSameId(item.id, message.senderId)) {
                    found = true;
                    return { ...item, count: item.count + 1, createdAt: message.createdAt || new Date().toISOString(), lastMessage: previewChatMessage(message) };
                }
            }
            return item;
        });

        if (!found) {
            updated.push({
                ...message,
                count: 1,
                id: targetId || message.senderId,
                isGroup: isGroup,
                // Ensure caseName is present if provided in message
                caseName: message.caseName,
                createdAt: message.createdAt || new Date().toISOString(),
                lastMessage: previewChatMessage(message)
            });
        }

        // Sort by createdAt
        updated.sort((a, b) => new Date(b.createdAt || b.created).getTime() - new Date(a.createdAt || a.created).getTime());

        this.unreadSubject.next(updated.filter(item => Number(item?.count || 0) > 0));
    }

    togglePin(userId: string, caseId: string, isPinned: boolean, userIdToPin: string): Promise<any> {
        if (isPinned) {
            return this.pinUser(userId, caseId, userIdToPin);
        } else {
            return this.unpinUser(userId, caseId, userIdToPin);
        }
    }

    private pinUser(userId: string, caseId: string, userIdToPin: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('user', 'pinned/create',
                {
                    userId: userId,
                    caseId: caseId,
                    pinnedUserId: userIdToPin
                }, (data, error) => {
                    if (data) {
                        this.signalingService.publish(caseId, {
                            type: 'pinUser',
                            userId: userIdToPin,
                            caseId: caseId,
                            isPinned: true
                        }, this.signalingService.pinUserTopicName);
                        resolve(true);
                    }
                    reject(error);
                });
        });
    }

    private unpinUser(userId: string, caseId: string, userIdToUnpin: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('user', 'pinned/delete',
                {
                    userId: userId,
                    caseId: caseId,
                    pinnedUserId: userIdToUnpin
                }, (data, error) => {
                    if (data) {
                        this.signalingService.publish(caseId, {
                            type: 'unpinUser',
                            userId: userIdToUnpin,
                            caseId: caseId,
                            isPinned: false
                        }, this.signalingService.pinUserTopicName);
                        resolve(true);
                    }
                    reject(error);
                });
        });
    }

    getPinnedUsers(userId: string, caseId: string) {
        this.requestService.getRequest('user', `pinned/get/${userId}/${caseId}`, (data, error) => {
            if (data && data.results?.length) {
                this.pinnedChats.next(data.results[0].pinnedUsers);
            }
        });
    }
}
